/**
 * <ShopGestureCard> — un geste de la boutique : un arbre planté, un repas offert (organism).
 *
 * Ce que la boutique montre d'un geste aujourd'hui : son image, son nom, sa phrase, combien de
 * fois l'élève l'a déjà fait, son prix en pétales, et « Acheter ». L'image vient du serveur ; à
 * défaut, un glyphe du registre dans une pastille.
 *
 * Ses états arrivent en props : assez de pétales ou non (`canAfford` ; sinon le bouton est inerte
 * et la carte le dit), achat en cours (`purchasing`), déjà acheté (`purchasedCount`, en vert :
 * c'est un acquis). `description` ajoute le texte long de la fiche du geste.
 *
 * Le prix s'écrit comme dans la fiche du geste (`formatAmount`) : un nombre et son unité, en gras,
 * un cran au-dessus du texte, derrière le pétale plein du jeu sur sa pastille.
 *
 * `onOpen` rend le haut de la carte pressable : il ouvre la fiche du geste, que l'élève doit
 * pouvoir lire même sans assez de pétales (le bouton est alors inerte).
 */
import React from "react"
import { Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Badge } from "../../atoms/Badge"
import { Button } from "../../atoms/Buttons/Button"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon } from "../../atoms/icons/Icon"
import { IconBubble } from "../../atoms/icons/IconBubble"
import { MediaThumbnail } from "../../atoms/MediaThumbnail"
import { Text } from "../../atoms/typography/Text"
import { Card } from "../../molecules/Card"
import { GAME_GLYPH_SIZE, GameGlyph } from "../../molecules/GameGlyph"
import { formatAmount } from "../../utils/gameAmount"
import { type UIShopGesture } from "../../../ports/primary/ui/types"

export type { UIShopGesture }

export type ShopGestureCardProps = {
    gesture: UIShopGesture
    /** Combien de fois l'élève a déjà fait ce geste. Défaut 0 : rien ne s'affiche. */
    purchasedCount?: number
    /** L'élève a assez de pétales. */
    canAfford: boolean
    /** L'achat est parti : le bouton tourne. Défaut `false`. */
    purchasing?: boolean
    onBuy?: () => void
    /** Ouvre la fiche du geste : le haut de la carte presse. Absent (défaut) : seul le bouton presse. */
    onOpen?: () => void
    testID?: string
}

/** Le côté de l'image d'un geste. */
export const GESTURE_IMAGE_SIZE = 72

/** Les mots d'un geste : la carte et sa fiche (`<ShopGestureSheet>`) disent les mêmes. */
export const SHOP_GESTURE_TEXTS = {
    buy: "Acheter",
    notEnough: "Tu n'as pas assez de pétales",
    purchased: (count: number) => `Acheté ${count} fois`,
}
const TEXTS = SHOP_GESTURE_TEXTS

/** L'image d'un geste — sa photo, ou à défaut son glyphe dans une pastille — à la taille donnée. */
export function GestureImage(props: { gesture: UIShopGesture; size: number }) {
    const { gesture, size } = props
    const { name, imageUrl, iconName = "gift" } = gesture

    return imageUrl != null && imageUrl !== "" ? (
        <MediaThumbnail uri={imageUrl} size={size} accessibilityLabel={name} />
    ) : (
        <IconBubble iconName={iconName} size={size} iconSize={Math.round(size * 0.45)} />
    )
}

export function ShopGestureCard(props: ShopGestureCardProps) {
    const { gesture, purchasedCount = 0, canAfford, purchasing = false, onBuy, onOpen, testID } = props
    const { name, shortDescription, description, price } = gesture

    const hasDescription = description != null && description !== ""

    const head = (
        <Row gap="3" align="start">
            <GestureImage gesture={gesture} size={GESTURE_IMAGE_SIZE} />
            <Stack gap="1" style={styles.body}>
                <Text size="text-lg" weight="bold">
                    {name}
                </Text>
                <Text size="text-md" color="text-muted">
                    {shortDescription}
                </Text>
                {purchasedCount > 0 && (
                    <Badge text={TEXTS.purchased(purchasedCount)} variant="success" tinted iconName="check" />
                )}
            </Stack>
            {onOpen != null && (
                <View style={styles.chevron}>
                    <Icon name="chevron-right" size={16} color="text-muted" />
                </View>
            )}
        </Row>
    )

    return (
        <Card testID={testID} p="4">
            <Stack gap="3">
                {onOpen != null ? (
                    <Pressable
                        onPress={onOpen}
                        accessibilityRole="button"
                        accessibilityLabel={name}
                        style={({ pressed }) => (pressed ? styles.pressed : undefined)}
                    >
                        {head}
                    </Pressable>
                ) : (
                    head
                )}

                {hasDescription && <Text size="text-md">{description}</Text>}

                <Row gap="3" justify="between">
                    <Row gap="2" style={styles.price}>
                        <GameGlyph name="petals" size={GAME_GLYPH_SIZE.row} />
                        <Text size="text-lg" weight="bold" numberOfLines={1}>
                            {formatAmount(price, "petals")}
                        </Text>
                    </Row>
                    <Button label={TEXTS.buy} onPress={onBuy} disabled={!canAfford} loading={purchasing} />
                </Row>

                {!canAfford && (
                    <Text size="text-sm" color="text-muted" style={styles.notice}>
                        {TEXTS.notEnough}
                    </Text>
                )}
            </Stack>
        </Card>
    )
}

const styles = StyleSheet.create(() => ({
    body: { flex: 1, flexShrink: 1, minWidth: 0 },
    chevron: { flexShrink: 0, alignSelf: "center" },
    pressed: { opacity: 0.6 },
    /** Un prix ne se coupe pas. */
    price: { flexShrink: 0 },
    /** Sous le bouton qu'elle explique. */
    notice: { textAlign: "right" },
}))
